import React, { useEffect, useState } from 'react';
import { App as AntdApp, Form, Input, Button, Card, Typography, Divider, Space } from 'antd';
import {
  MailOutlined,
  LockOutlined,
  WechatOutlined,
  GithubOutlined,
  GoogleOutlined,
  SafetyCertificateOutlined,
  ArrowLeftOutlined,
} from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useNavigate, Link, useLocation, useSearchParams } from 'react-router-dom';
import { authAPI, storeTokens, siteAPI } from '../services/api';
import AuthBrandPanel from '../components/auth/AuthBrandPanel';
import { AUTH_PAGE_STYLES } from '../components/auth/authPageStyles';
import { useSiteBranding } from '../hooks/useSiteBranding';

const { Title, Text } = Typography;

const API_BASE = process.env.REACT_APP_API_URL || '';

const OAUTH_BUTTONS: Record<string, { icon: React.ReactNode; style?: React.CSSProperties }> = {
  wechat: { icon: <WechatOutlined />, style: { color: '#07c160' } },
  github: { icon: <GithubOutlined /> },
  google: { icon: <GoogleOutlined />, style: { color: '#4285f4' } },
};

const Login: React.FC = () => {
  const { message } = AntdApp.useApp();
  const { login } = useAuth();
  const { mode } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [oauthProviders, setOauthProviders] = useState<string[]>([]);
  const [logoFailed, setLogoFailed] = useState(false);
  const { slogan, siteName, userLoginLogoUrl } = useSiteBranding();

  const from = (location.state as any)?.from?.pathname || '/';

  useEffect(() => {
    const oauthTicket = searchParams.get('oauth_ticket');
    if (!oauthTicket) return;
    let cancelled = false;
    authAPI.exchangeOAuthTicket(oauthTicket)
      .then(tokens => {
        if (cancelled) return;
        storeTokens(tokens.access_token, tokens.refresh_token);
        window.history.replaceState({}, document.title, '/login');
        window.location.href = '/';
      })
      .catch(() => {
        if (!cancelled) message.error('第三方登录票据无效或已过期，请重新登录');
      });
    return () => { cancelled = true; };
  }, [searchParams]);

  useEffect(() => {
    setLogoFailed(false);
  }, [userLoginLogoUrl]);

  useEffect(() => {
    siteAPI.getOAuthProviders().then(setOauthProviders).catch(() => {});
  }, []);

  useEffect(() => {
    if (searchParams.get('verified') === '1') {
      message.success('邮箱验证成功，请使用账号密码登录');
    }
    if (searchParams.get('reset') === '1') {
      message.success('密码已重置，请使用新密码登录');
    }
  }, [message, searchParams]);

  const onFinish = async (values: { email: string; password: string }) => {
    setLoading(true);
    try {
      const result = await login(values.email, values.password);
      if (result.daily_login_reward && result.daily_login_reward > 0) {
        message.success(`登录成功，每日登录奖励 ${result.daily_login_reward} 金币`);
      } else {
        message.success('登录成功');
      }
      navigate(from, { replace: true });
    } catch (err: any) {
      message.error(err?.response?.data?.detail || '登录失败');
    } finally {
      setLoading(false);
    }
  };

  const handleOAuth = (provider: string) => {
    window.location.href = `${API_BASE}/api/oauth/authorize/${provider}`;
  };

  return (
    <div className={`auth-page ${mode === 'dark' ? 'is-dark' : ''}`}>
      <style>{AUTH_PAGE_STYLES}</style>

      <AuthBrandPanel
        description={slogan}
        siteName={siteName}
        logoUrl={userLoginLogoUrl}
        logoFailed={logoFailed}
        onLogoError={() => setLogoFailed(true)}
      />

      <div className="auth-page-right">
        <div className="auth-shell">
          <Card className="auth-card" variant="borderless">
            <div className="auth-topbar">
              <div className="auth-badge">
                <SafetyCertificateOutlined />
                <span>安全账户登录</span>
              </div>
              <Link to="/" className="auth-back-link">
                <ArrowLeftOutlined style={{ marginRight: 6 }} />
                返回首页
              </Link>
            </div>

            <div className="auth-header">
              <Title className="auth-title">欢迎回来</Title>
              
            </div>

            <Form onFinish={onFinish} size="large" className="auth-form" layout="vertical">
              <Form.Item
                name="email"
                rules={[
                  { required: true, message: '请输入邮箱' },
                  { type: 'email', message: '请输入有效邮箱' },
                ]}
              >
                <Input
                  className="auth-input"
                  prefix={<MailOutlined />}
                  placeholder="邮箱"
                  autoComplete="email"
                />
              </Form.Item>

              <Form.Item name="password" rules={[{ required: true, message: '请输入密码' }]}>
                <Input.Password
                  className="auth-input"
                  prefix={<LockOutlined />}
                  placeholder="密码"
                  autoComplete="current-password"
                />
	              </Form.Item>

	              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 18 }}>
	                <Link to="/verify-email" className="auth-back-link">
	                  收不到验证码？
	                </Link>
	                <Link to="/forgot-password" className="auth-back-link">
	                  忘记密码
	                </Link>
	              </div>

	              <Form.Item style={{ marginBottom: 0 }}>
                <Button className="auth-submit" type="primary" htmlType="submit" loading={loading} block>
                  登录
                </Button>
              </Form.Item>
            </Form>

            {oauthProviders.length > 0 && (
              <>
                <Divider className="auth-divider" plain>
                  <Text type="secondary">社交账号登录</Text>
                </Divider>
                <Space size={14} style={{ justifyContent: 'center', display: 'flex' }} wrap>
                  {oauthProviders.map((provider) => {
                    const buttonConfig = OAUTH_BUTTONS[provider];
                    if (!buttonConfig) {
                      return null;
                    }
                    return (
                      <Button
                        key={provider}
                        shape="circle"
                        size="large"
                        icon={buttonConfig.icon}
                        style={buttonConfig.style}
                        onClick={() => handleOAuth(provider)}
                      />
                    );
                  })}
                </Space>
              </>
            )}

            <Divider className="auth-divider" plain>
              <Text type="secondary">还没有账号？</Text>
            </Divider>
            <div className="auth-link-copy">
              <Link to="/register" className="auth-link-accent">
                立即注册
              </Link>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Login;
